/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:04]
Permissions to update outside the main branch: [false]
*/

import { Button, Form, Modal, Pending, requiredRule, ValidatedInput } from '@finbeat/react-toolkit';
import { datetimeWithSecondsFormat } from 'constans/dateFormat';
import { format } from 'date-fns';
import useAuth from 'hooks/useAuth';
import { Procedure } from 'models/api/procedure';
import {
    TariffProcedureParamAdd,
    TariffProcedureParamValue,
    TariffProcedureParamValueChange,
} from 'models/api/tariffProcedureParam';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useTariffProcedureParamsWorkplaceState } from './TariffProcedureParams';
import TariffProcedureParamsList from './TariffProcedureParamsList';

export type TariffProcedureParamsFormType = Omit<TariffProcedureParamValue, 'paramValue'> & {
    paramValue: string | boolean;
};

export type TariffProcedureParamFormType = Omit<TariffProcedureParamAdd, 'params'> & {
    params: TariffProcedureParamsFormType[];
};

type TariffProcedureParamChangeModalProps = {
    isOpen?: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
    procedures: Procedure[];
    isLoading: boolean;
};

const TariffProcedureParamChangeModal = ({
    isOpen = false,
    toggle,
    procedures,
    isLoading,
}: TariffProcedureParamChangeModalProps) => {
    const { t } = useTranslation();
    const { hasRole } = useAuth();
    const { changeItemMutation, addItemMutation, currentItem } =
        useTariffProcedureParamsWorkplaceState();

    const getDefaultParams = (procedureId: number) =>
        procedures
            .find((p) => p.id === procedureId)
            ?.params.map((i) => {
                let value: string | boolean = '';
                if (i.selectOptions.length) {
                    value = i.selectOptions[0].value;
                } else if (i.type === 'bool') {
                    value = false;
                } else if (i.type === 'datetime') {
                    value = format(new Date(), datetimeWithSecondsFormat);
                }

                return {
                    procedureParamId: i.id,
                    paramValue: value,
                    selectOptions: i.selectOptions,
                    paramName: i.name,
                    type: i.type,
                };
            }) ?? [];

    const getCurrentParams = () =>
        currentItem
            ? currentItem.params.map((i) => ({
                  id: i.id,
                  procedureParamId: i.procedureParamId,
                  paramValue:
                      i.type === 'bool' ? i.paramValue.toLowerCase() === 'true' : i.paramValue,
                  selectOptions: i.selectOptions,
                  paramName: i.paramName,
                  type: i.type,
              }))
            : [];

    const defaultValue = useMemo(() => {
        let procedureId = 0;
        if (currentItem) {
            procedureId = currentItem.procedureId;
        } else if (procedures.length) {
            procedureId = procedures[0].id;
        }

        return {
            name: currentItem ? currentItem.name : '',
            dsc: currentItem ? currentItem.dsc : '',
            procedureId: procedureId,
            params: currentItem ? getCurrentParams() : getDefaultParams(procedureId),
        };
    }, [currentItem, procedures]);

    const saveChanges = async (data: TariffProcedureParamFormType) => {
        const params: TariffProcedureParamValueChange[] = data.params.map((p) => ({
            paramValue: p.paramValue.toString(),
            procedureParamId: p.procedureParamId,
            id: p.id,
        }));

        currentItem
            ? changeItemMutation.mutate({ ...data, id: currentItem.id, params: params })
            : addItemMutation.mutate({ ...data, params: params });

        toggle(false);
    };

    const formHasRole = (addRole: string, editRole: string): boolean =>
        currentItem ? hasRole(editRole) : hasRole(addRole);

    const disabled = !formHasRole('tariffProcedureParamsCreate', 'tariffProcedureParamsUpdate');

    return (
        <Modal
            size="lg"
            isOpen={isOpen}
            toggle={() => toggle(false)}
            scrollable
            className="modal-with-piker"
            data-testid="tariff-procedure-param-modal"
        >
            <Form<TariffProcedureParamFormType>
                onSubmit={(data) => saveChanges(data)}
                defaultValues={defaultValue}
                className="form-in-modal"
            >
                <ModalHeader toggle={() => toggle(false)}>
                    {currentItem ? `#${currentItem.id}` : t('BackOfficeNew')}
                </ModalHeader>
                <ModalBody className="custom-scrollbar">
                    <Row>
                        <Col>
                            <ValidatedInput<TariffProcedureParamFormType>
                                name="name"
                                label={t('BackOfficeName')}
                                rules={{ required: requiredRule }}
                                disabled={disabled}
                            />
                        </Col>
                        <Col>
                            <ValidatedInput<TariffProcedureParamFormType>
                                type="select"
                                id="tpp-procedure-name"
                                name="procedureId"
                                label={t('BackOfficeProcedureName')}
                                options={procedures.map((i) => (
                                    <option value={i.id} key={i.id}>
                                        {i.name}
                                    </option>
                                ))}
                                rules={{ required: requiredRule }}
                                disabled={disabled}
                            />
                        </Col>
                    </Row>
                    <hr />
                    {isLoading ? (
                        <Pending />
                    ) : (
                        <TariffProcedureParamsList
                            getCurrentParams={getCurrentParams}
                            getDefaultParams={getDefaultParams}
                            disabled={disabled}
                        />
                    )}
                    <hr />
                    <ValidatedInput<TariffProcedureParamFormType>
                        name="dsc"
                        label={t('BackOfficeDescription')}
                        type="textarea"
                        disabled={disabled}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => toggle(false)}>
                        <Trans i18nKey="BackOfficeCancel" />
                    </Button>
                    <Button color="success" disabled={disabled}>
                        <Trans i18nKey="BackOfficeSave" />
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default TariffProcedureParamChangeModal;
